import auth from '@feathersjs/authentication-client';
import type { Application } from '@feathersjs/feathers';
import feathers from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import rx from 'feathers-reactive';
import io from 'socket.io-client';

import { API_ENDPOINT, globalCache } from '@sbrc/utils';

import { setupSessionLifecycle } from '../auth/sessionLifecycle';

import { reactiveMatcher } from './reactiveMatcher';
import { retryOnNotAuthenticated, retryOnTimeout } from './rxOperators';
import type { Services } from './Services';

export function getApp() {
  return globalCache('feathersApp', () => {
    const socket = io(API_ENDPOINT, {
      path: '/api',
      transports: ['websocket'],
    });

    const app = feathers<Services>();

    app.configure(socketio(socket));
    app.configure(auth());

    app.configure(
      rx({
        idField: 'id',
        // Ignore server-only query flags (e.g. `includeHidden`) in client-side
        // real-time matching. The default siftMatcher only strips `$`-keys, so
        // such a flag would evict every matched row on a patch event. See
        // reactiveMatcher.ts.
        matcher: reactiveMatcher,
        // retryOnNotAuthenticated re-subscribes a watch stream after a
        // transient NotAuthenticated (reconnect/token gap) instead of
        // completing it permanently — preventing the SUP-4067/SUP-4064
        // "stale until refresh" surfaces. See rxOperators.ts.
        pipe: [retryOnNotAuthenticated(), retryOnTimeout()],
      }),
    );

    // Install the session-expiry kick exactly once, at the moment the
    // feathers-client is first constructed. `globalCache` guarantees this
    // factory runs once per page; `setupSessionLifecycle` is additionally
    // idempotent via its own `initialized` guard. The app instance is
    // passed in so `sessionLifecycle.ts` doesn't have to import this
    // module (avoiding an `import/no-cycle` violation).
    setupSessionLifecycle(app);

    return app as Omit<Application<Services>, 'io'> & {
      io: SocketIOClient.Socket;
    };
  });
}
